<style lang="scss">
    .autocomplete-result {
        .project-name {
            font-size: 1.2em !important;
        }
        .project-address {
            color: #888;
        }
    }

    .property-selection {
        cursor: pointer;
        background: blue;
        &.selected {

        }
    }

    legend {
        font-size: 1.4em;
        font-weight: bold;

        small {
            font-weight: normal;
            color: #666;
            text-transform: uppercase;
            font-size: 0.7em;
        }
    }

    .autocomplete input {
        padding: 22px 10px !important;
        font-size: 1.2em;
    }

    p small {
        text-transform: uppercase;
    }
</style>

<template>
    <div v-if="application && project">
        <ValidationObserver
            v-slot="{ handleSubmit, invalid, dirty }"
            ref="propertyAddressForm"
        >
            <form class="uk-form" @submit.prevent="handleSubmit(create)" autocomplete="chrome-off">
                <!-- Tenant Address -->
                <fieldset>
                    <legend class="app-fades">
                        Tenant Address
                        <small>Physical address of the property that requires assistance</small>
                    </legend>
                </fieldset>
                <div class="uk-grid">

                    <!-- Property Type -->
                    <div class="uk-width-1-1 uk-width-medium-1-1">
                        <p class="uk-margin-bottom-remove"><small>PROPERTY TYPE</small></p>
                        <select v-model="applicant.property_address.property_type_id" class="uk-form-large uk-width-1-1">
                            <option :value="null">Select a Property Type</option>
                            <option v-for="type in assets.propertyTypes" :value="type.id">{{ type.name }}</option>
                        </select>
                    </div>
                    <!-- / Property Type -->

                    <div class="uk-width-1-1" v-if="applicant.property_address.property_type_id != null">
                        <div class="uk-grid">
                            <!-- Neighborhood Autocomplete -->
                            <div class="uk-width-1-1 uk-width-medium-1-1 uk-margin-top" v-if="applicant.property_address.property_type_id === 1">
                                <p class="uk-margin-bottom-remove"><small>Neighborhood / Apartment Complex / Subdivision</small></p>
                                <autocomplete
                                        :placeholder="'Find My Neighborhood...'"
                                        ref="autocomplete"
                                        class="form-autocomplete uk-form-large"
                                        :search="search"
                                        :get-result-value="getResultValue"
                                        :debounce-time="200"
                                        :default-value="project.project_name"
                                        @submit="setProperty"
                                        @blur="setPropertyFromBlur"
                                >
                                    <template #result="{result, props}">
                                        <li v-bind="props" class="autocomplete-result property-result">
                                            <div class="project-name">{{ result.project_name }}</div>
                                            <div class="project-address" v-if="result.address">
                                                {{ result.address.line_1 }} | {{ result.address.city }}, {{ result.address.state }} {{ result.address.zip }}
                                            </div>
                                        </li>
                                    </template>
                                </autocomplete>
                            </div>

                            <!-- Building -->
                            <div class="uk-width-1-1 uk-width-medium-1-2 uk-margin-small-top" v-if="applicant.property_address.property_type_id === 1">
                                <div class="uk-grid">
                                    <div class="uk-width-1-1 uk-width-medium-1-2" :class="{'uk-medium-1-1': application.building_id !== null}">
                                        <p class="uk-margin-bottom-remove"><small>Select your Building</small></p>
                                        <select v-model="application.building_id" class="uk-form-large uk-width-1-1">
                                            <option :value="null">Select a Building</option>
                                            <option v-for="building in project.buildings" :value="building.id">{{ building.building_name }}</option>
                                            <option :value="'other'">Enter Building Name ...</option>
                                        </select>
                                    </div>
                                    <div class="uk-width-1-1 uk-width-medium-1-2" v-show="application.building_id === 'other'">
                                        <p class="uk-margin-bottom-remove"><small>Building Name</small></p>
                                        <input type="text" v-model="newBuildingName" class="uk-form-large uk-width-1-1" placeholder="Building Name">
                                    </div>
                                </div>

                            </div>
                            <!-- / Building -->

                            <!-- Unit -->
                            <div class="uk-width-1-1 uk-width-medium-1-2 uk-width-small-1-1 uk-margin-small-top" v-if="applicant.property_address.property_type_id === 1">
                                <div class="uk-grid">
                                    <div class="uk-width-1-1 uk-width-medium-1-2">
                                        <p class="uk-margin-bottom-remove"><small>Select your Unit</small></p>
                                        <select v-model="application.unit_id" class="uk-form-large uk-width-1-1">
                                            <option :value="null">Select a Unit</option>
                                            <option v-for="unit in unitSelectOptions" :value="unit.id">{{ unit.unit_name }}</option>
                                            <option value="other">Enter Unit Name...</option>
                                        </select>
                                    </div>
                                    <div class="uk-width-1-1 uk-width-medium-1-2" v-show="application.unit_id === 'other'">
                                        <p class="uk-margin-bottom-remove"><small> Unit Name</small></p>
                                        <TextInput
                                                v-model="newUnitName"
                                                name="Unit Name"
                                                placeholder="Unit Name"
                                                @input="update"
                                        />
                                    </div>
                                </div>
                            </div>
                            <!-- / Unit -->

                            <!-- Property Address Line 1 -->
                            <div class="uk-width-1-1 uk-width-medium-1-2 uk-width-small-1-1 uk-margin-small-top">
                                <p class="uk-margin-bottom-remove"><small>Property Address</small></p>
                                <TextInput
                                        v-model="applicant.property_address.line_1"
                                        name="Line1"
                                        placeholder="Street Address"
                                        rules="required"
                                        @input="update"
                                />
                            </div>
                            <!-- / Property Address Line 1 -->

                            <!-- Property Address Line 2 -->
                            <div class="uk-width-1-1 uk-width-medium-1-2 uk-width-small-1-1 uk-margin-small-top" v-show="applicant.property_address.property_type_id !== 1">
                                <p class="uk-margin-bottom-remove"><small>&nbsp;Address Line 2</small></p>
                                <TextInput
                                        v-model="applicant.property_address.line_2"
                                        name="Line 2"
                                        placeholder="Address Line 2"
                                        @input="update"
                                />
                            </div>
                            <!-- / Property Address Line 2 -->

                            <!-- City -->
                            <div class="uk-width-1-1 uk-width-medium-1-2 uk-margin-small-top">
                                <p class="uk-margin-bottom-remove"><small>City</small></p>
                                <TextInput
                                        v-model="applicant.property_address.city"
                                        name="City"
                                        placeholder="City"
                                        rules="required"
                                        @input="update"
                                />
                            </div>
                            <!-- / City -->

                            <!-- State -->
                            <div class="uk-width-1-1 uk-width-medium-1-2 uk-margin-small-top">
                                <p class="uk-margin-bottom-remove"><small>State</small></p>
                                <select v-model="applicant.property_address.state_id" class="uk-width-1-1 state-select uk-button uk-form-select uk-form-large">
                                    <option v-for="item in assets.states" :value="item.id" :key="item.id">{{item.state_name }}</option>
                                </select>
                            </div>
                            <!-- / State -->

                            <!-- Zip -->
                            <div class="uk-width-1-1 uk-width-medium-1-2 uk-margin-small-top">
                                <p class="uk-margin-bottom-remove"><small>Zip</small></p>
                                <TextInput
                                        v-model="applicant.property_address.zip"
                                        name="Zip"
                                        placeholder="Zip"
                                        rules="required"
                                        @input="update"
                                />
                            </div>
                            <!-- / Zip -->
                        </div>
                    </div>
                </div>
                <!-- / Tenant Address -->

                <!-- Project Address -->
                <fieldset v-if="applicant.property_address.property_type_id != null" class="uk-margin-large-top">
                    <legend class="app-fades">
                        Property Office
                        <small>Name and mailing address of the property management office</small>
                    </legend>
                </fieldset>
                <div v-if="applicant.property_address.property_type_id != null" class="uk-grid">
                    <div class="uk-width-1-1">

                    </div>
                    <div class="uk-width-1-2">
                        <select v-model="project.physical_address_id" class="uk-form-large uk-width-1-1">
                            <option :value="-1">I don't know</option>
                            <option v-for="address in selectionAddresses" :value="address.id">{{ address.line_1 }} | {{ address.city }}, {{ address.state }} {{ address.zip }}</option>
                            <option :value="null">I know some or all of my property office's information</option>
                        </select>
                    </div>
                    <div class="uk-width-1-2">
                        <div class="uk-margin-small-bottom">
                            <phone-number-input
                                    name="Property Manager Phone"
                                    v-model="projectPhone"
                                    @onValidated="updateProjectPhone"

                            ></phone-number-input>
                        </div>

                        <address-form v-if="project.physical_address_id === null"
                                      @update="updateProjectAddress"
                                      @phoneUpdated="updateProjectPhone"
                                      :address="project.address"
                                      :show-phone="false"
                                      :show-person="false"></address-form>
                    </div>
                </div>
                <!-- / Project Address -->

                <!-- Property Owner -->
                <fieldset v-if="applicant.property_address.property_type_id != null" class="uk-margin-large-top">
                    <legend class="app-fades">
                        Property Owner
                        <small>Name and mailing address of the property owner</small>
                    </legend>
                </fieldset>
                <div v-if="applicant.property_address.property_type_id != null" class="uk-grid">
                    <div class="uk-width-1-1">

                    </div>
                    <div class="uk-width-1-2">
                        <select v-model="poAddressType" class="uk-form-large uk-width-1-1">
                            <option :value="-1">I don't know</option>
                            <option :value="null">I have my Property Owner's Information</option>
                        </select>
                    </div>
                    <div class="uk-width-1-2">
                        <div v-if="poAddressType === null" class="uk-grid">
                            <address-form
                                    class="uk-width-1-1"
                                    :address="poAddress"
                                    :person="renterOwner"
                                    :organization="project.owner_organization ? project.owner_organization : {}"
                                    @update="updatePoAddress"
                                    @personUpdated="updatePropertyOwner"
                                    @organizationUpdated="updatePoOrganization"
                                    :show-person="true"></address-form>
                        </div>
                    </div>
                </div>
                <!-- / Property Owner -->

                <!-- Property Manager -->
                <fieldset v-if="applicant.property_address.property_type_id != null" class="uk-margin-large-top">
                    <legend class="app-fades">
                        Property Manager
                        <small>Name and mailing address of the property manager</small>
                    </legend>
                </fieldset>
                <div v-if="applicant.property_address.property_type_id != null" class="uk-grid">
                    <div class="uk-width-1-2">
                        <select v-model="pmAddressType" class="uk-form-large uk-width-1-1">
                            <option :value="-1">I don't know</option>
                            <option :value="null">I have my Property Manager's Information</option>
                        </select>
                    </div>
                    <div class="uk-width-1-2">
                        <div v-if="pmAddressType === null" class="uk-grid">
                            <address-form
                                    class="uk-width-1-1"
                                    :address="pmAddress"
                                    :person="renterManager"
                                    :organization="project.manager_organization ? project.manager_organization : {}"
                                    @update="updatePmAddress"
                                    @personUpdated="updatePropertyManager"
                                    @organizationUpdated="updatePmOrganization"
                                    :show-person="true"></address-form>
                        </div>
                    </div>
                </div>
                <!-- / Property Manager -->
            </form>
        </ValidationObserver>

        <add-property-modal ref="addPropertyModal"></add-property-modal>
    </div>
</template>

<script>
    import Autocomplete from '@trevoreyre/autocomplete-vue';
    import AddPropertyModal from "../../../../../components/AppWizard/Step1/AddPropertyModal";
    import AddressForm from "../../../../../components/AppWizard/Step1/AddressForm";
    import {mapState} from "vuex";
    import PropertyContactForm from "../../../../../components/AppWizard/Step2/PropertyContactForm";
    export default {
        name: "PropertyAddress",
        components: {
            PropertyContactForm,
            AddressForm,
            AddPropertyModal,
            Autocomplete
        },
        data() {
            return {

                newBuildingName: '',
                newUnitName: '',

                pmAddressType: null,
                poAddressType: null,
                projectAddressType: -1,
                buildingId: null,
                pmAddress: {},
                poAddress: {},
                projectPhone: '',
            }
        },
        computed: {
            unitSelectOptions() {
                if(!this.application.building_id) return [];
                let units = [];
                _.each(this.project.units, unit => {
                    if(unit.building_id === this.application.building_id) {
                        units.push(unit);
                    }
                });
                return units;
            },
            selectionAddresses() {
                let addresses = [];
                if(this.applicant.property_address) {
                    addresses.push(this.applicant.property_address);
                }
                if(this.poAddress.line_1) {
                    addresses.push(this.poAddress);
                }
                if(this.pmAddress.line_1) {
                    addresses.push(this.pmAddress);
                }
                if(this.project.address && !addresses.includes(this.project.address)) {
                    addresses.push(this.project.address);
                }
                return addresses;
            },
            ...mapState('Application', {
                assets: state => state.assets,
                applicant: state => state.applicant,
                application: state => state.application,
                renterOwner: state => state.renterOwner,
                renterManager: state => state.renterManager,
                project: state => state.project,
            })
        },
        watch: {
            'application.building_id': function(newValue) {
                if(newValue === 'other') {
                    this.application.unit_id = 'other';
                }else {
                    this.application.unit_id = null;
                }
            },
            'application.project': function(newValue, oldValue) {
                if(newValue.id && oldValue.id && newValue.id != oldValue.id) {
                    this.application.building_id = null;
                    this.application.unit_id = null;
                }

            },
        },
        mounted() {
            if(this.renterManager && this.renterManager.address) {
                this.pmAddress = this.renterManager.address;
            }
            if(this.renterOwner && this.renterOwner.address) {
                this.poAddress = this.renterOwner.address;
            }
            if(this.project && this.project.phone_number) {
                this.projectPhone = `(${this.project.phone_number.area_code}) ${this.project.phone_number.phone_number}`
            }
        },
        methods: {
            async search(term) {
                if(term.length < 2) return [];
                let r = await axios.get('/api/hope/property/search?q='+term);
                // r.data.push({
                //     id: null,
                //     project_name: 'Create New',
                // })
                return r.data;
            },
            getResultValue(result) {
                return `${result.project_name}`;
            },

            createNewProject(param)
            {
                return this.$store.dispatch('Application/createProject', param).then(async res => {
                    await this.save();
                    this.$store.dispatch('Application/fetch', this.application.id);
                })
            },

            async setPropertyFromBlur($event) {
                console.log('blur', $event);
                let q = $event.target.value;
                let r = await axios.get('/api/hope/property/validate?q='+q);

                let confirmationText = '';
                let confirmationTitle = '';
                if(r.data.properties.length === 0) {
                    confirmationText = `Please double check that the name "${q}" is correct.`;
                    confirmationTitle = 'Please Confirm Your Property Name'

                    this.$swal({
                        title: confirmationTitle,
                        text: confirmationText,
                        showCancelButton: true,
                        confirmButtonText: 'Yes, I\'m sure.'
                    }).then(r => {
                        if(r.isConfirmed) {
                            const param = { name: q, application_id: this.application.id }
                            this.createNewProject()
                        }
                    }).catch(e => {
                        this.$refs.autocomplete.value = '';
                    })
                }



                console.log(r);
            },
            async setProperty(result) {
                // if(result.id === null) {
                //     this.$refs.addPropertyModal.open();
                //     return;
                // }
                this.project = result;
                this.application.project_id = result.id;
                this.$emit('selected', result);
                await this.save();
                this.$store.dispatch('Application/fetch', this.application.id);
                console.log('Setting property: ', result);
            },

            async searchPerson(query) {
                // Search People
                let url = '/api/hope/applications/'+this.applicationId+'/find-landlord';
                let r = await axios.get(url+'?query='+query);
                r.data.push({
                    id: null,
                    first_name: 'Add New',
                    last_name: 'Contact',
                })
                return r.data;
            },

            updateProjectAddress(address) {
                console.log('updating to ', address);
                this.project.address = address;
            },
            updateProjectPhone(phone) {
                console.log('updating to ', phone);
                this.project.phone_number = {
                    area_code: phone.number.significant.substr(0, 3),
                    phone_number: phone.number.significant.substr(3, 10),
                };
                console.log(this.project.phone);
            },
            updatePoAddress(address) {
                console.log('updating to ', address);
                this.poAddress = address;
            },
            updatePoOrganization(organization) {
                // set org
                console.log('setting owner organization to', organization);
                axios.post(`/api/hope/applications/${this.application.id}/set-owner-organization/${organization.id}`);
                this.$store.dispatch('Application/fetch', this.application.id);
            },
            async updatePropertyOwner(person) {
                await this.save();
                // Update application property manager and reload
                let url = `/api/hope/applications/${this.application.id}/set-contact`;
                let data = {
                    person_id: person.id,
                    application_role_id: 4,
                }

                let r = await axios.post(url, data);

                await this.$store.dispatch('Application/fetch', this.application.id);

            },
            updatePmAddress(address) {
                console.log('updating to ', address);
                this.pmAddress = address;
            },
            updatePmOrganization(organization) {
                // set org
                console.log('setting manager organization to', organization);
                axios.post(`/api/hope/applications/${this.application.id}/set-manager-organization/${organization.id}`);
                this.$store.dispatch('Application/fetch', this.application.id);
            },
            async updatePropertyManager(person) {
                await this.save();
                // Update application property manager and reload
                let url = `/api/hope/applications/${this.application.id}/set-contact`;
                let data = {
                    person_id: person.id,
                    application_role_id: 3,
                }

                let r = await axios.post(url, data);

                await this.$store.dispatch('Application/fetch', this.application.id);

            },


            async validateStep() {
                let stepValid = false

                await this.$refs.propertyAddressForm.validate().then(async (isValid) => {
                    if (isValid) {
                        stepValid = await this.save()
                    }
                })
                return stepValid

            },

            async save() {
                // Resolve Project Id
                let enteredPropertyName = null;
                if (this.applicant.property_address.property_type_id === 1)
                {
                    enteredPropertyName = this.$refs.autocomplete.value;
                    if(this.application.project && this.application.project.project_name === enteredPropertyName) {
                        enteredPropertyName = null;
                    }
                }

                if (this.applicant.property_address.property_type_id === 2)
                {
                    const param = { name: this.applicant.property_address.line_1, application_id: this.application.id }
                    await this.createNewProject(param)
                }

                let data = {
                    // Project Information
                    project_id: this.application.project_id,
                    new_project_name: enteredPropertyName,

                    // Save Property Information
                    address: this.applicant.property_address,
                    building_id: this.application.building_id,
                    new_building_name: this.application.building_id === 'other' ? this.newBuildingName : null,
                    unit_id: this.application.unit_id,
                    new_unit_name: this.application.unit_id === 'other' ? this.newUnitName : null,

                    // Property Office
                    property_office_address_id: this.project.physical_address_id,
                    property_office_address: this.project.address,
                    property_office_phone: this.project.phone_number,

                    // Property Manager
                }

                if (this.application.project_id)
                {
                    let r = await axios.put(`/api/hope/applications/${this.application.id}/step-property-address`, data);
                    this.$store.dispatch('Application/fetch', this.application.id);
                }

                return true;
            },
        }
    }
</script>
